/* Facet bar disclosure.
 *
 * The RESTING state belongs to CSS: above the breakpoint the panel is a flex row and the trigger is
 * display:none; below it the panel is display:none until it carries [data-open]. That ordering is
 * the whole point. An earlier version shipped the panel open and let this module collapse it after
 * the module executed — a fourth serial asset behind opensku.css — so on a 3G connection in Amman
 * the collection page painted 554px of filters and then yanked them away above the grid. That was
 * the largest layout shift in the theme, on its most-visited page.
 *
 * A bar that already has filters applied is rendered [data-open] by Liquid, so a shopper never has
 * to hunt for their own active selections behind a closed trigger — and that state, too, is correct
 * on the first frame rather than being fixed up by script afterwards.
 *
 * Script here does exactly one thing: respond to a tap.
 */
document.addEventListener('click', (e) => {
  const trigger = e.target.closest('[data-facet-toggle]')
  if (!trigger) return
  const panel = document.getElementById(trigger.getAttribute('aria-controls'))
  if (!panel) return
  trigger.setAttribute('aria-expanded', String(panel.toggleAttribute('data-open')))
})


/* ---------------------------------------------------------------- applying a filter

   Ticking a facet did NOTHING with JavaScript enabled. The form carries no submit button outside
   <noscript>, and no script read the `data-url-add` / `data-url-remove` attributes that Shopify
   renders onto every value — so the box checked, the URL never changed, and the grid never
   filtered. Verified on the live preview: clicking `filter.v.availability` left location.href
   untouched while the element's data-url-add held the exact destination.

   Shopify's own URLs are authoritative: they carry the other active filters, the search terms and
   the sort order, so navigating to them is both simpler and more correct than rebuilding a query
   string here. Price is different — it is two free-text fields rather than a pre-rendered link —
   so that one submits the form, which serialises every checked box and the hidden sort input. */

const form = document.getElementById('OpenSkuFacets')

function busy(on) {
  // The grid is about to be replaced by a page load. Saying so is the difference between
  // "applied" and "did my click register?" on a slow connection.
  document.querySelector('.grid')?.toggleAttribute('data-busy', on)
  form?.setAttribute('aria-busy', String(on))
}

/* ADMITTING THE PRESS.
 *
 * busy() was reachable from exactly two controls — a facet checkbox and the debounced price submit.
 * Sort, a filter pill, clear-all and pagination each replace the WHOLE 48-tile grid and said nothing,
 * so on a slow connection the shopper's only evidence was that nothing had happened yet. Four more
 * triggers into the ONE pending vocabulary; no second vocabulary is introduced. */
document.addEventListener('submit', (e) => {
  // Delegated, because sort's inline handler is requestSubmit() — see sections/collection.liquid.
  // The price debounce submits #OpenSkuFacets, class `facets`, so it cannot match `.sorter` here.
  if (e.target.closest('.sorter')) busy(true)
})

document.addEventListener('click', (e) => {
  if (!e.target.closest('.facets__pill, .facets__clear, .pager a')) return
  /* Never claim a navigation that is not going to happen. A modified click opens a new tab and leaves
     THIS page exactly where it is, so dimming would strand the grid at opacity .55 and
     pointer-events: none with nothing en route to replace it. Same guard as opensku-quickview.js. */
  if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
  busy(true)
})

/* Press Back after paginating and the browser restores the page from bfcache with the DOM exactly as
   it was left — including data-busy, i.e. a permanently dimmed, permanently inert grid. Pagination
   and pill-removal are precisely the controls a shopper backs out of, so this is required by the two
   listeners above, not optional hygiene. Unconditional rather than gated on e.persisted, because some
   browsers restore session-history DOM state without setting it; on a fresh load it is a no-op. */
window.addEventListener('pageshow', () => busy(false))

form?.addEventListener('change', (e) => {
  const el = e.target
  if (el.type === 'checkbox') {
    const url = el.checked ? el.dataset.urlAdd : el.dataset.urlRemove
    if (!url) return
    busy(true)
    location.assign(url)
  }
})

/* Price is typed, not clicked, so it waits for the shopper to stop rather than navigating on every
   keystroke. `change` alone would miss a value the shopper types and then abandons without
   blurring, so both events feed the same debounce. */
let priceTimer
for (const input of form?.querySelectorAll('.facets__price input') || []) {
  const go = () => {
    clearTimeout(priceTimer)
    priceTimer = setTimeout(() => { busy(true); form.requestSubmit() }, 550)
  }
  input.addEventListener('input', go)
  input.addEventListener('change', go)
}


/* ---------------------------------------------------------------- the chip popovers

   Above 760px each facet is a <details> chip whose panel is absolutely positioned. Two behaviours
   that markup does not give for free, and nothing else:

   1. ONE AT A TIME. Two overlapping popovers is a bug, not a feature.
   2. DISMISS. Click outside or press Escape. A popover you can only close by finding the chip again
      is a trap, and this one sits directly over the grid it filters.

   Both are gated on the desktop presentation. Below the breakpoint the groups are a stacked list
   inside the filter drawer, where several open at once is the correct and useful state — closing a
   shopper's other facets as they open a third would be actively hostile there. */
const chipsActive = () => matchMedia('(min-width: 761px)').matches

document.addEventListener('click', (e) => {
  if (!chipsActive()) return
  const summary = e.target.closest('.facets__summary')
  const group = summary?.closest('.facets__group')

  for (const d of document.querySelectorAll('.facets__group[open]')) {
    /* Never close the one being opened, and never close the one being used — a click on a checkbox
       inside a panel must not dismiss the panel out from under the pointer. */
    if (d === group || d.contains(e.target)) continue
    d.removeAttribute('open')
  }
})

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !chipsActive()) return
  /* The group the shopper is IN, not the first one in the document. Several groups ship server-
     rendered `open` (price, plus every list facet with <=14 values), so on a typical collection page
     Escape closed a panel the shopper was not using and dragged focus backwards to its chip. */
  const open = document.activeElement?.closest('.facets__group[open]')
    || document.querySelector('.facets__group[open]')
  if (!open) return
  open.removeAttribute('open')
  /* Focus goes back to the chip that owns the panel. Dropping it to <body> would strand a keyboard
     shopper at the top of the document, above the rail, on every dismiss. */
  open.querySelector('.facets__summary')?.focus()
})
